/**
 * TimerControls - Start/Pause/Resume/Cancel buttons for the Pomodoro timer.
 *
 * Reads state from usePomodoroStore to determine which buttons to show:
 * - Idle: "Start Focus" button + optional "Start Break" (if sessions completed)
 * - Running: "Pause" button
 * - Paused: "Resume" button + "Cancel" button
 *
 * All buttons have aria-labels, visible focus rings, and 44x44px minimum touch targets.
 * Style: rounded-full buttons with icons.
 *
 * @see Requirements 7.2, 7.5, 7.7
 */

import { usePomodoroStore } from '@/stores';

// ─── Icon Components ─────────────────────────────────────────────────────────

function PlayIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M6.5 4.5v11l9-5.5-9-5.5z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="5" y="4" width="3.5" height="12" rx="1" />
      <rect x="11.5" y="4" width="3.5" height="12" rx="1" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <rect x="5" y="5" width="10" height="10" rx="2" />
    </svg>
  );
}

function CoffeeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 8h10v6a3 3 0 01-3 3H7a3 3 0 01-3-3V8z" />
      <path d="M14 9h1.5a2 2 0 010 4H14" />
      <path d="M7 4v2M9 3v3M11 4v2" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TimerControls() {
  const { phase, isRunning, isPaused, sessionCount } = usePomodoroStore();
  const { startWork, startBreak, pause, resume, cancel } = usePomodoroStore();

  // Base button styles: rounded-full, 44px min touch target, visible focus ring
  const baseButtonClass = `
    inline-flex items-center justify-center gap-2
    min-w-[44px] min-h-[44px] px-6 py-3
    rounded-full font-medium text-body
    transition-all duration-default ease-ease-default
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2
    disabled:opacity-50 disabled:pointer-events-none
  `;

  const primaryButtonClass = `
    ${baseButtonClass}
    bg-primary text-white shadow-card
    hover:shadow-hover hover:bg-primary-dark active:bg-primary-dark
  `;

  const secondaryButtonClass = `
    ${baseButtonClass}
    bg-secondary-light text-text-primary border border-border
    hover:bg-secondary hover:text-white active:bg-secondary
  `;

  const ghostButtonClass = `
    ${baseButtonClass}
    bg-transparent text-text-secondary border border-border
    hover:bg-hover-bg hover:text-text-primary active:bg-bg-secondary
  `;

  // ─── Idle State ──────────────────────────────────────────────────────────
  if (phase === 'idle') {
    return (
      <div className="flex items-center gap-3" role="group" aria-label="Timer controls">
        <button
          className={primaryButtonClass}
          onClick={() => {
            const { linkedTaskId, linkedTaskTitle } = usePomodoroStore.getState();
            startWork(linkedTaskId ?? undefined, linkedTaskTitle ?? undefined);
          }}
          aria-label="Start focus session"
        >
          <PlayIcon />
          <span>Start Focus</span>
        </button>

        {sessionCount > 0 && (
          <button
            className={secondaryButtonClass}
            onClick={() => startBreak()}
            aria-label="Start break"
          >
            <CoffeeIcon />
            <span>Take Break</span>
          </button>
        )}
      </div>
    );
  }

  // ─── Running State ───────────────────────────────────────────────────────
  if (isRunning && !isPaused) {
    return (
      <div className="flex items-center gap-3" role="group" aria-label="Timer controls">
        <button
          className={secondaryButtonClass}
          onClick={pause}
          aria-label="Pause timer"
        >
          <PauseIcon />
          <span>Pause</span>
        </button>
      </div>
    );
  }

  // ─── Paused State ────────────────────────────────────────────────────────
  if (isPaused) {
    return (
      <div className="flex items-center gap-3" role="group" aria-label="Timer controls">
        <button
          className={primaryButtonClass}
          onClick={resume}
          aria-label="Resume timer"
        >
          <PlayIcon />
          <span>Resume</span>
        </button>
        <button
          className={ghostButtonClass}
          onClick={() => void cancel()}
          aria-label="Cancel session"
        >
          <StopIcon />
          <span>Cancel</span>
        </button>
      </div>
    );
  }

  // Fallback (shouldn't reach here)
  return null;
}

TimerControls.displayName = 'TimerControls';
